import { Routes, Route, Navigate } from 'react-router-dom';
import { SettingGuard, ReportGuard, AuditLogGuard } from '@/components/PermissionGuard';
import { DashboardPage } from '@/pages/dashboard';
import { ProjectsPage } from '@/pages/projects';
import { ProjectDetailsPage } from '@/pages/project-details';
import { PortfolioPage } from '@/pages/portfolio';
import { UtilisationPage } from '@/pages/utilisation';
import { WIPReportingPage } from '@/pages/wip-reporting';
import { ProjectStatusReportPage } from "@/pages/project-status-report";
import { BudgetVarianceReportPage } from "@/pages/budget-variance-report";
import { ProjectPLReportPage } from "@/pages/project-pl-report";
import { ProjectForecastReportPage } from "@/pages/project-forecast-report";
import { ProfitabilityReportPage } from "@/pages/profitability-report";
import { DirectoryPage } from '@/pages/directory';
import { RolesRatesPage } from '@/pages/roles-rates';
import { SkillsPage } from '@/pages/skills';
import { GanttPage } from '@/pages/gantt';
import { ProjectTransactionsPage } from '@/pages/project-transactions';
import SettingsGeneral from '@/pages/settings-general';
import SettingsProject from '@/pages/settings-project';
import SettingsUserPreferences from '@/pages/settings-user-preferences';
import SettingsIntegrationsPage from '@/pages/settings-integrations';
import SettingsFeatureLabsPage from '@/pages/settings-feature-labs';
import SettingsUserPermissionsPage from '@/pages/settings-user-permissions';
import SettingsEditRolePage from '@/pages/settings-edit-role';
import ProjectTemplatesPage from '@/pages/project-templates';
import SettingsTemplates from '@/pages/settings/settings-templates';
import SettingsNotificationsPage from '@/pages/settings-notifications';
import { SettingsPage as SettingsHub } from '@/pages/settings-hub';



import { UserPreferencesPage } from '@/pages/user-preferences';
import { AuditLogPage } from '@/pages/reports-audit-log';
import { ProjectCreationSetup } from '@/pages/project-creation-setup';
import { ErrorHandlingDemoPage } from '@/pages/error-handling-demo';
import { ColumnConfiguratorDemo } from '@/pages/column-configurator-demo';
import { ResponsiveTableDemo } from '@/pages/responsive-table-demo';
import { DataTableDemo } from '@/pages/data-table-demo';

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/projects" element={<ProjectsPage />} />
      <Route path="/projects/:projectId" element={<ProjectDetailsPage />} />
      <Route path="/portfolio" element={<PortfolioPage />} />
      <Route path="/utilisation" element={<ReportGuard report="utilisationReport"><UtilisationPage /></ReportGuard>} />
      <Route path="/reports/wip" element={<ReportGuard report="wipReport"><WIPReportingPage /></ReportGuard>} />
      <Route path="/reports/project-status" element={<ReportGuard report="projectStatusReport"><ProjectStatusReportPage /></ReportGuard>} />
      <Route path="/reports/budget-variance" element={<ReportGuard report="budgetVarianceReport"><BudgetVarianceReportPage /></ReportGuard>} />
      <Route path="/reports/project-pl" element={<ReportGuard report="plReport"><ProjectPLReportPage /></ReportGuard>} />
      <Route path="/reports/project-forecast" element={<ReportGuard report="projectForecastReport"><ProjectForecastReportPage /></ReportGuard>} />
      <Route path="/reports/profitability" element={<ReportGuard report="profitabilityReport"><ProfitabilityReportPage /></ReportGuard>} />
      <Route path="/wip-reporting" element={<Navigate to="/reports/wip" replace />} />
      <Route path="/profitability" element={<Navigate to="/reports/profitability" replace />} />

<Route path="/directory" element={<DirectoryPage />} />
      <Route path="/roles-rates" element={<RolesRatesPage />} />
      <Route path="/skills" element={<SkillsPage />} />
      <Route path="/gantt" element={<GanttPage />} />
      <Route path="/project-transactions" element={<ReportGuard report="projectTransactionsReport"><ProjectTransactionsPage /></ReportGuard>} />
      <Route path="/audit-log" element={<AuditLogGuard><AuditLogPage /></AuditLogGuard>} />
      <Route path="/settings" element={<SettingsHub />} />
      <Route path="/settings/general" element={<SettingGuard setting="generalSettings"><SettingsGeneral /></SettingGuard>} />
      <Route path="/settings/project" element={<SettingGuard setting="projectSettings"><SettingsProject /></SettingGuard>} />
      <Route path="/settings/user-preferences" element={<SettingsUserPreferences />} />
      <Route path="/settings/integrations" element={<SettingGuard setting="integrations"><SettingsIntegrationsPage /></SettingGuard>} />
      <Route path="/settings/feature-labs" element={<SettingsFeatureLabsPage />} />
      <Route path="/settings/user-permissions" element={<SettingGuard setting="rolesAndPermissions"><SettingsUserPermissionsPage /></SettingGuard>} />
      <Route path="/settings/user-permissions/:roleId" element={<SettingGuard setting="rolesAndPermissions"><SettingsEditRolePage /></SettingGuard>} />
      <Route path="/settings/templates" element={<SettingGuard setting="templates"><SettingsTemplates /></SettingGuard>} />
      <Route path="/settings/notifications" element={<SettingGuard setting="notifications"><SettingsNotificationsPage /></SettingGuard>} />
      <Route path="/settings/finance" element={<Navigate to="/settings/general" replace />} />

      <Route path="/project-templates" element={<ProjectTemplatesPage />} />
      <Route path="/user-preferences" element={<UserPreferencesPage />} />
      <Route path="/project-creation-setup" element={<ProjectCreationSetup />} />
      <Route path="/error-handling-demo" element={<ErrorHandlingDemoPage />} />
      <Route path="/column-configurator-demo" element={<ColumnConfiguratorDemo />} />
      <Route path="/responsive-table-demo" element={<ResponsiveTableDemo />} />
      <Route path="/data-table-demo" element={<DataTableDemo />} />
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

// Route path constants for easy reference
export const ROUTES = {
  DASHBOARD: '/dashboard',
  PROJECTS: '/projects',
  PROJECT_DETAILS: (projectId: number | string) => `/projects/${projectId}`,
  PORTFOLIO: '/portfolio',
  UTILISATION: '/utilisation',
  WIP_REPORTING: '/reports/wip',
  PROJECT_STATUS_REPORT: '/reports/project-status',
  BUDGET_VARIANCE_REPORT: '/reports/budget-variance',
  PROJECT_PL_REPORT: '/reports/project-pl',
  PROJECT_FORECAST_REPORT: '/reports/project-forecast',
  PROFITABILITY: '/reports/profitability',
DIRECTORY: '/directory',
  ROLES_RATES: '/roles-rates',
  SKILLS: '/skills',
  GANTT: '/gantt',
  PROJECT_TRANSACTIONS: '/project-transactions',
  AUDIT_LOG: '/audit-log',
  SETTINGS: '/settings/general',
  SETTINGS_GENERAL: '/settings/general',
  SETTINGS_PROJECT: '/settings/project',
  SETTINGS_USER_PREFERENCES: '/settings/user-preferences',
  SETTINGS_INTEGRATIONS: '/settings/integrations',
  SETTINGS_FEATURE_LABS: '/settings/feature-labs',
  SETTINGS_USER_PERMISSIONS: '/settings/user-permissions',
  SETTINGS_TEMPLATES: '/settings/templates',
  SETTINGS_NOTIFICATIONS: '/settings/notifications',
  SETTINGS_PROJECT_CATEGORY_MAPPING: '/settings/project/category-mapping',
  PROJECT_TEMPLATES: '/project-templates',
  USER_PREFERENCES: '/user-preferences',
  PROJECT_CREATION_SETUP: '/project-creation-setup',
  ERROR_HANDLING_DEMO: '/error-handling-demo',
  COLUMN_CONFIGURATOR_DEMO: '/column-configurator-demo',
  RESPONSIVE_TABLE_DEMO: '/responsive-table-demo',
  DATA_TABLE_DEMO: '/data-table-demo',
} as const;
